import { CruisesContract } from '@luxuryescapes/contract-svc-cruise'
import { OFFER_TYPE_CRUISE } from 'constants/offer'
import { filterDataByHighestPercentage } from 'lib/Cruise/cruiseUtils'
import { addDays, dateDifference, isoDateFormat } from 'lib/dateUtils'
import moment from 'moment'

export function cruisesMap(cruises: Array<CruisesContract.OfferSummary>): Array<App.CruiseOfferSummary> {
  return cruises.map((cruise) => {
    const ship = cruisesMapShip(cruise.ship)

    return {
      id: cruise.id,
      type: OFFER_TYPE_CRUISE,
      cruiseLine: {
        name: cruise.cruiseLine.name,
        imageId: cruise.cruiseLine.imageId,
      },
      disableDeposits: cruise.disableDeposits,
      duration: cruise.duration,
      leExclusive: cruise.leExclusive,
      heroImageId: cruise.heroImageId,
      name: cruise.displayName ?? cruise.name,
      departureDates: cruise.departureDates,
      departurePort: cruise.departurePort.name,
      returnPort: cruise.returnPort.name,
      ship,
      images: ship.images,
      lowestDeparturePrice: cruise.lowestDeparturePrice,
      description: cruise.leVisibleDescription,
      destinationDescription: cruise.destinationDescription,
      itinerary: cruise.itineraries?.map(cruiseItineraryItemMap).sort((a, b) => a.startDay - b.startDay),
    }
  })
}

export function cruiseLinesMap(cruiseLines: Array<CruisesContract.CruiseLine>): Array<App.CruiseLine> {
  return cruiseLines.map((cruiseLine) => ({
    name: cruiseLine.name,
    code: cruiseLine.code,
    imageId: cruiseLine.imageId,
  }))
}

function getCruiseProductType(cruise: CruisesContract.Offer): App.CruiseProductType {
  if (cruise.leExclusive) {
    return 'cruise-exclusive'
  }
  // todo: add when data comes through
  // else if (cruise.premium) {
  //   return 'cruise-tactical'
  // }
  else {
    return 'cruise'
  }
}

export function cruiseOfferMap(cruise: CruisesContract.Offer): App.CruiseOffer {
  const ship = cruisesMapShip(cruise.ship)
  const offerPromo = cruise.lowestDeparturePrice?.promotion as any

  return {
    id: cruise.id,
    productType: getCruiseProductType(cruise),
    cruiseLine: {
      name: cruise.cruiseLine.name,
      imageId: cruise.cruiseLine.imageId,
      vendorInfo: cruise.cruiseLine.vendorInfo,
    },
    duration: cruise.duration,
    disableDeposits: cruise.disableDeposits,
    heroImageId: cruise.heroImageId,
    name: cruise.displayName ?? cruise.name,
    departureDates: cruise.departureDates,
    departurePort: cruise.departurePort.name,
    showPricePerNight: cruise.showPricePerNight,
    returnPort: cruise.returnPort.name,
    ship,
    slug: cruise.id,
    type: OFFER_TYPE_CRUISE,
    parentType: OFFER_TYPE_CRUISE,
    analyticsType: OFFER_TYPE_CRUISE,
    images: cruise?.images?.map(cruisesMapOfferImages) ?? [],
    faq: [].map(cruiseFaqMap), // TODO: Implement faq for cruises
    finePrint: cruiseFinePrintMap(), // TODO: Implement finePrint for cruises
    departures: Object.keys(cruise.departures ?? {})
      .reduce((acc, cur) => ({
        ...acc,
        [cur]: cruise.departures?.[cur]?.map((item) => cruiseDepartureMap(item, cruise.duration)),
      }), {}),
    itinerary: cruise.itineraries?.map(cruiseItineraryItemMap).sort((a, b) => a.startDay - b.startDay),
    destinationDescription: cruise.destinationDescription,
    description: cruise.leVisibleDescription,
    // these types are not correct from server, cannot work for now. Any'ing them
    lowestDeparturePricesByCategory: cruise.lowestDeparturePricesByCategory as any,
    lowestDeparturePrice: cruise.lowestDeparturePrice as any,
    promotionDescriptionsByOffer: cruise.promotionDescriptionsByOffer,
    promotion: offerPromo && {
      ...offerPromo,
      isEndingIn: dateDifference(new Date(offerPromo.endDate), new Date()).days,
      isNew: dateDifference(new Date(), new Date(offerPromo.startDate)).days < 7,
    } as any,
  }
}

export function cruiseLowestPriceByMonthMap(
  prices: Array<CruisesContract.LowestPriceByMonth>,
  fromDate: string,
): Array<App.CruiseLowestPriceByMonth> {
  return prices.map((price): App.CruiseLowestPriceByMonth => ({
    year: moment(fromDate).year(),
    month: price.month,
    totalFare: Math.ceil(price.totalFare),
  }))
}

export function cruiseOrderItemMap(orderItem: any): App.CruiseOrderItem {
  return {
    sessionId: orderItem.session_id,
    bookingId: orderItem.booking_id,
    departureId: orderItem.departure_id,
    departureDate: orderItem.departure_date,
    arrivalDate: orderItem.arrival_date,
    itemId: orderItem.item_id,
    orderId: orderItem.order_id,
    reservationId: orderItem.reservation_id,
    status: orderItem.status,
    total: orderItem.total,
    cruiseOfferId: orderItem.cruiseOffer_id,
    transactionKey: orderItem.transactionKey,
    passengers: (orderItem.passengers ?? []).map(cruiseOrderItemTravellerMap),
  }
}

export function cruiseOrderItemTravellerMap(traveller) {
  return {
    firstName: traveller.first_name,
    lastName: traveller.last_name,
    type: traveller.type,
    isLeadPassenger: traveller.is_lead_passenger,
  }
}

const cruisesMapShip = (ship: CruisesContract.Ship): App.CruiseShip => ({
  id: ship.id,
  name: ship.name,
  images: ship.images ? ship.images.map(cruisesMapShipImages) : [],
  shipInfo: ship.shipInfo?.map(cruisesMapShipInfo) ?? [],
  ...(ship.decks?.length && { decks: ship.decks?.map(cruisesShipDeckMap) }),
  ...(ship.cabinCategories?.length && { cabinCategories: ship.cabinCategories?.map(cruisesMapCabinCategories) }),
  occupancies: {
    ...(!!ship.occupancies?.maxOccupancy && { maxOccupancy: ship.occupancies.maxOccupancy }),
    ...(!!ship.occupancies?.maxAdults && { maxAdults: ship.occupancies.maxAdults }),
    ...(!!ship.occupancies?.maxChildren && { maxChildren: ship.occupancies.maxChildren }),
    ...(!!ship.occupancies?.maxInfants && { maxInfants: ship.occupancies.maxInfants }),
    ...(!!ship.occupancies?.childMaximumAge && { childMaximumAge: ship.occupancies.childMaximumAge }),
    ...(!!ship.occupancies?.infantMaximumAge && { infantMaximumAge: ship.occupancies.infantMaximumAge }),
  },
})

const cruisesMapCabinCategories = (cabinCategory: CruisesContract.ShipCabinCategory): App.CruiseShipCabinCategory => ({
  id: cabinCategory.id,
  cabinType: cabinCategory.cabinType,
  code: cabinCategory.code,
  ...(cabinCategory.color && { color: cabinCategory.color }),
  ...(cabinCategory.imageId && { imageId: cabinCategory.imageId }),
  ...(cabinCategory.cabinCategoryInfo?.length && {
    cabinCategoryInfo: cabinCategory.cabinCategoryInfo?.map(cruisesMapCabinInfo),
  }),
})

export const cruisesShipDeckMap = (deck: CruisesContract.ShipDeck): App.CruiseShipDeck => ({
  id: deck.id,
  name: deck.name,
  deckNumber: deck.deckNumber,
  externalId: deck.externalId,
  ...(deck.imageId && { imageId: deck.imageId }),
  ...(deck.shipCabinPosition && { shipCabinPosition: deck.shipCabinPosition }),
})

const cruisesMapCabinInfo = (cabinInfo: CruisesContract.ShipCabinCategoryInfo): App.CruiseShipCabinCategoryInfo => ({
  id: cabinInfo.id,
  name: cabinInfo.name,
  description: cabinInfo.description,
  languageCode: cabinInfo.languageCode,
})

const cruisesMapShipInfo = (shipInfo: CruisesContract.ShipInfo): App.CruiseShipInfo => ({
  title: shipInfo.title,
  description: shipInfo.description,
  dataTypeName: shipInfo.dataTypeName,
})

const cruisesMapShipImages = (image: CruisesContract.ShipImage): App.CruiseShipImage => ({
  id: (image.idImage || image.imageId) as string,
  order: image.order,
})

const cruisesMapOfferImages = (image: CruisesContract.OfferImage): App.CruiseShipImage => ({
  id: image.imageId,
  order: image.order,
})

const cruiseFaqMap = (faq: any): App.CruiseFAQ => ({ // CruisesContract.Faq
  title: faq.title,
  content: faq.content,
  key: faq.key,
})

const cruiseFinePrintMap = (finePrint?: any): App.CruiseFinePrint => ({ // CruisesContract.FinePrint
  finalParagraph: finePrint?.finalParagraph,
  sections: finePrint?.sections?.map(cruiseFinePrintSectionMap) ?? [],
})

const cruiseFinePrintSectionMap = (finePrintSection: any): App.CruiseFinePrintSection => ({ // CruisesContract.FinePrintSection
  description: finePrintSection.introductoryText,
  items: finePrintSection.items ?? [],
  subSections: finePrintSection.subSections?.map(cruiseFinePrintSubSectionMap) ?? [],
  title: finePrintSection.topic,
})

const cruiseFinePrintSubSectionMap = (finePrintSubSection: any): App.CruiseFinePrintSection => ({ // CruisesContract.FinePrintSection
  items: finePrintSubSection.subSectionItems ?? [],
  title: finePrintSubSection.title,
})

const cruisePricesMap = (price: CruisesContract.Price): App.CruisePrice => ({
  marketName: price.marketName,
  currencyCode: price.currencyCode,
  categoryCode: price.categoryCode,
  adultFare: Number(price.adultFare),
  tax: Number(price.tax),
  total: Math.round(price.total),
  isTaxIncluded: price.isTaxIncluded,
  cabinCategory: price.cabinCategory,
  cabinPriceDesc: price.cabinPriceDesc ?? '',
  nccf: Number(price.nccf) ?? undefined,
  isNccfIncluded: price.isNccfIncluded,
  ...(price.promotionDescription && {
    promotionDescription: price.promotionDescription,
  }),
})

const cruiseItineraryItemMap = (itineraryItem: CruisesContract.Itinerary): App.CruiseItineraryItem => {
  const mappedPort = cruiseItineraryItemPortMap(itineraryItem.port)

  return {
    startDay: itineraryItem.dayNumber,
    title: mappedPort?.portInfo?.map(item => item.title).join(', ') ?? '',
    description: mappedPort?.portInfo?.map(item => item.description).join() ?? '',
    startTime: itineraryItem.arrivalTime,
    endTime: itineraryItem.departureTime,
    port: mappedPort,
  }
}

const cruiseItineraryItemPortMap = (port?: CruisesContract.FullPort): App.CruisePort | null => {
  if (!port) return null

  return {
    name: port.name,
    code: port.code,
    nearestAirportCode: port.nearestAirportCode,
    address: port.address,
    city: port.city,
    stateProvince: port.stateProvince,
    countryCode: port.countryCode,
    latitude: port.latitude,
    longitude: port.longitude,
    portInfo: port.portInfo?.map(cruiseItineraryItemPortInfoMap) ?? [],
  }
}

const cruiseItineraryItemPortInfoMap = (portInfo: CruisesContract.PortInfo): App.CruiseItineraryItemPortInfo => ({
  title: portInfo.title,
  description: portInfo.description,
  dataTypeName: portInfo.dataTypeName,
})

const cruiseDepartureMap = (departure: CruisesContract.Departure, duration: number): App.CruiseDeparture => {
  return {
    id: departure.id,
    externalId: departure.externalId,
    departureDate: departure.departureDate,
    arrivalDate: calcArrivalDate(departure.departureDate, duration),
    prices: departure.prices?.map(cruisePricesMap) ?? [],
    ...(departure.promotionDescriptionsByDeparture && {
      promotionDescriptionsByDeparture: departure.promotionDescriptionsByDeparture,
    }),
  }
}

// BOOKING FLOW MAPPERS

export const cruiseCabinAvailabilityMap = (
  availableCabins: CruisesContract.CabinAvailability,
): App.CruiseCabinAvailabilityResponse => {
  const cabins: Array<App.CruiseCabinAvailability> = availableCabins.cabins.map(cabin => ({
    cabinNumber: cabin.cabinNumber,
    bedType: cabin.bedType ?? '',
    deckId: cabin.deckId ?? '',
    deckName: cabin.deckName,
    minimumPassengers: cabin.minimumPassengers,
    maximumPassengers: cabin.maximumPassengers,
    locationDescription: cabin.locationDescription,
    isAccessibleCabin: cabin.isAccessibleCabin,
    isCabinInfoAvailable: cabin.isCabinInfoAvailable,
    connectingCabinNumber: cabin.connectingCabinNumber,
  }))

  const mapLegend: Array<App.ShipMapLegend> | undefined = availableCabins.mapLegend

  return {
    cabins,
    mapLegend,
    ...(availableCabins.cabinHold && {
      cabinHold: cruiseCabinHoldMap(availableCabins.cabinHold),
    }),
  }
}

export const cabinViewMap = (
  cruiseOffer: App.CruiseOffer,
): Array<App.CruiseCabinView> => {
  const { cabinCategories } = cruiseOffer.ship
  const categories: App.CruiseShipCabinCategory[] = Object.assign(cabinCategories || [])
  const cabinView: App.CruiseCabinView[] = categories.map((category, i) => {
    const categoryInfo = category.cabinCategoryInfo?.[0] ?? null

    return {
      id: category.id,
      cabin: {
        code: category.code,
        color: category.color ?? '',
        imageId: category.imageId ?? '',
        typeName: category.cabinType,
        categoryInfo: {
          id: categoryInfo?.id ?? i,
          name: categoryInfo?.name ?? category.cabinType,
          description: categoryInfo?.description ?? '',
          languageCode: categoryInfo?.languageCode ?? '',
        },
      },
    }
  })

  return cabinView
}

export const lowestRatePricePerCabinTypeMap = (
  cabinRatesView: Array<App.CruiseCabinRateView>,
): Array<App.CruiseLowestPriceCabin> => {
  const cabinKey: { [k: string] : App.CruiseLowestPriceCabin } = {}

  const cabins = cabinRatesView.reduce((
    acc: App.CruiseLowestPriceCabin[],
    cur: App.CruiseCabinRateView) => {
    const hasItem = acc.find(item => item.typeName === cur.cabinType)

    hasItem ?
      cabinKey[cur.cabinType] = {
        typeName: cur.cabinType,
        lowestPrice: Math.min(hasItem.lowestPrice, cur.ratePrices.price),
      } :
      cabinKey[cur.cabinType] = {
        typeName: cur.cabinType,
        lowestPrice: cur.ratePrices.price,
      }

    return Object.values(cabinKey).map(item => item)
  }, []).sort((a, b) => a.lowestPrice - b.lowestPrice)

  return cabins
}

export const cruiseCabinRatesMap = (
  cabinRate: CruisesContract.CabinRateResponse,
): App.CruiseCabinRateResponse => {
  return {
    sessionId: cabinRate.sessionId,
    bookingId: cabinRate.bookingId,
    departureId: cabinRate.departureId,
    cabinHoldTimeout: cabinRate.cabinHoldTimeout,
    cabins: cabinRate.cabins.map(cabin => {
      const hasBrochureInfo = !!(cabin.rate.brochurePrice && cabin.rate.brochureDiscountPrice && cabin.rate.brochureDiscountPercentage)
      const hasOnboardCredits = !!(cabin.rate.onboardCreditAmount && cabin.rate.onboardCreditCurrency)

      return {
        promotionTitle: cabin.promotionTitle,
        applicablePromotion: cabin.applicablePromotion,
        cabinType: cabin.type,
        rateCode: cabin.rate.code,
        ratePrices: {
          taxes: cabin.rate.prices.taxes,
          price: cabin.rate.prices.price,
          total: cabin.rate.prices.total, /* @deprecated -> use price instead */
          portCharges: cabin.rate.prices.portCharges,
          vendorPrice: cabin.rate.prices.vendorPrice,
          taxesAndPortCharges: cabin.rate.prices.taxesAndPortCharges,
          isNccfIncluded: cabin.rate.prices.isNccfIncluded,
          isTaxesIncluded: cabin.rate.prices.isTaxesIncluded,
        },
        componentId: cabin.rate.componentId,
        categoryName: cabin.rate.categoryName,
        isNonRefundable: cabin.rate.isNonRefundable,
        rateCodeDescription: cabin.rate.codeDescription,
        pricedCategoryCode: cabin.rate.pricedCategoryCode,
        ...(hasOnboardCredits && {
          onboardCredits: {
            amount: cabin.rate.onboardCreditAmount as number,
            currencyCode: cabin.rate.onboardCreditCurrency as string,
          },
        }),
        ...(hasBrochureInfo && {
          brochureInfo: {
            price: cabin.rate.brochurePrice as number,
            discountPrice: cabin.rate.brochureDiscountPrice as number,
            discountPercentage: cabin.rate.brochureDiscountPercentage as number,
          },
        }),
        status: cabin.status,
      }
    }),
  }
}

export const cruiseCabinRateDetailsMap = (
  details: string,
): App.CruiseCabinRateDetailsResponse => {
  const list = details?.match(/<li>.*?<\/li>/g) ?? []

  const tempElement = document.createElement('div')
  tempElement.innerHTML = details
  const paragraphs = tempElement.getElementsByTagName('p')
  let termsAndConditions = ''
  for (let i = 0; i < paragraphs.length; i++) {
    const paragraph = paragraphs[i]
    if (paragraph.textContent) {
      termsAndConditions = paragraph.textContent.trim()
      break
    }
  }

  const FilteredList = list.map(
    item => {
      if ((item.toLocaleLowerCase().includes('terms') && item.toLocaleLowerCase().includes('conditions')) && termsAndConditions) {
        return `<li><button>${item.match(/<li>(.*?)<\/li>/s)?.[1]}</button></li>`
      }
      return item
    },
  )

  const preview = `<ul>${(FilteredList.slice(0, 6).join(''))}</ul>`
  const fullList = `<ul>${(FilteredList.join(''))}</ul>`

  return {
    rateDetails: fullList,
    detailsPreview: preview,
    hasMoreDetails: list.length > 6,
    termsAndConditions,
  }
}

export const cruiseCabinHoldMap = (
  cabinHold: CruisesContract.CabinHoldResponse,
): App.CruiseCabinHold => {
  const isNonRefundable = cabinHold.pricing.isNonRefundable || false
  const onboardCredit = cabinHold.pricing.onboardCredit || null

  return {
    pricing: {
      currency: cabinHold.pricing.currency,
      total: cabinHold.pricing.total,
      taxesAndFees: cabinHold?.pricing?.tax?.amount || 0,
      passengerPricing: cabinHold.pricing.passengerPricing.map(cruiseCabinHoldPassengerMap),
      cancellationPolicy: filterDataByHighestPercentage(cabinHold.pricing.paymentSchedule, isNonRefundable),
      paymentSchedule: cabinHold.pricing.paymentSchedule,
      ...(!!onboardCredit && {
        onboardCredits: {
          amount: onboardCredit.amount,
          currencyCode: onboardCredit.currency,
        },
      }),
    },
    ...(cabinHold?.alternateDiningRequired && { alternateDiningRequired: cabinHold.alternateDiningRequired }),
    ...(cabinHold?.bedConfigurations && { bedConfigurations: cabinHold.bedConfigurations }),
    ...(cabinHold?.tableSizes && { tableSizes: cabinHold.tableSizes }),
    ...(cabinHold?.diningOptions && { diningOptions: cabinHold.diningOptions }),
    ...(cabinHold?.prepaidGratuities && { prepaidGratuities: cabinHold.prepaidGratuities }),
    ...(cabinHold?.insurance && { insurance: cabinHold.insurance.map(cruiseBuildCabinHoldInsurance) }),
    ...(cabinHold?.vendorOptionSets && { vendorOptionSets: cabinHold.vendorOptionSets.map(cruiseBuildVendorOptionSet) }),
    ...(cabinHold?.vendorAddedOptionSets && { vendorAddedOptionSets: cabinHold.vendorAddedOptionSets.map(cruiseBuildVendorOptionSet) }),
  }
}

const cruiseBuildCabinHoldInsurance = (option: CruisesContract.CabinInsurance): App.CruiseCabinHoldInsurance => ({
  code: option.code,
  name: option.name,
  status: option.status,
  totalPrice: option.totalPrice,
})

const cruiseBuildVendorOptionSet = (
  option: CruisesContract.CabinVendorOptionSet,
): App.CruiseCabinHoldCabinVendorOptionSet => ({
  name: option.name,
  priceType: option.priceType,
  maximumNumberPerPassenger: option.maximumNumberPerPassenger,
  isSelectableByPassenger: option.isSelectableByPassenger,
  optionList: option.optionList.map(item => ({
    componentId: item.componentId,
    name: item.name,
    status: item.status,
    price: item.price,
    description: item.description,
    startDate: item.startDate,
    endDate: item.endDate,
  })),
})

const cruiseCabinHoldPassengerMap = (
  passengerPricing: CruisesContract.PassengerPricing,
): App.CruisePassengerPricing => ({
  passengerIndex: passengerPricing.passengerIndex,
  total: passengerPricing.total,
})

export const cruiseBookingInfoMap = (
  booking: CruisesContract.BookingOrderResponse,
): App.CruiseBookingInfo => {
  const travellersInfo = booking.passengers?.details || booking.travellersInfo
  return {
    id: booking.id,
    departureId: booking.departureId,
    orderId: booking.orderId,
    totalAmount: booking.totalAmount,
    categoryId: booking.categoryId,
    cabinNumber: booking.cabinNumber,
    departureDate: booking.departureDate,
    arrivalDate: booking.arrivalDate,
    status: booking.status,
    cabinCode: booking.cabinCode,
    sessionId: booking.sessionId,
    categoryCode: booking.berthedCategoryCode,
    confirmationNumber: booking.confirmationNumber,
    cabinTypeName: booking.sailingBerthedCategoryName,
    dining: booking.dining,
    alternateDining: booking.alternateDining,
    tableSize: booking.tableSize,
    bedConfiguration: booking.bedConfiguration,
    prepaidGratuities: booking.prepaidGratuities,
    insurance: booking.insurance,
    ...(!!booking.rateCodeDetails && { rateCodeDetails: cruiseCabinRateDetailsMap(booking.rateCodeDetails) }),
    ...(!!travellersInfo && { travelersInfo: getTravellersInfoFromBooking(travellersInfo) }),
  }
}

type TravellerView = CruisesContract.TravellerRepositoryInfo
  | CruisesContract.BookingRetrievePassengerDetails

const getTravellersInfoFromBooking = (
  travellers: Array<TravellerView>,
): App.TravelerInfo[] => {
  return travellers.map((item: TravellerView) => ({
    type: item.type,
    firstName: item.firstName,
    lastName: item.lastName,
  }))
}

export const cruiseDepartureByIdMap = (
  departure: CruisesContract.DepartureByIdResponse,
): App.CruiseDepartureView => ({
  id: departure.id,
  duration: departure.duration,
  returnDate: departure.returnDate,
  departureDate: departure.departureDate,
  offer: {
    id: departure.offer.id,
    name: departure.offer.name,
    samePorts: departure.offer.samePorts,
    returnPortName: departure.offer.returnPortName,
    departurePortName: departure.offer.departurePortName,
    numberOfCities: departure.offer.numberOfCities,
    numberOfCountries: departure.offer.numberOfCountries,
  },
  vendor: {
    id: departure.vendor.id,
    name: departure.vendor.name,
    imageId: departure.vendor.imageId,
  },
  ship: {
    id: departure.ship.id,
    name: departure.ship.name,
    imageId: departure.ship.imageId,
  },
})

export const cruiseCabinPricingMap = (
  cabinPricing: CruisesContract.CabinPricingResponse,
): App.CruiseCabinPricing => {
  const { onboardCredit } = cabinPricing.pricing
  const isNonRefundable = cabinPricing.pricing.isNonRefundable || false

  return {
    pricing: {
      currency: cabinPricing.pricing.currency,
      total: cabinPricing.pricing.total,
      taxesAndFees: cabinPricing?.pricing?.tax?.amount || 0,
      passengerPricing: cabinPricing.pricing.passengerPricing.map(cruiseCabinHoldPassengerMap),
      cancellationPolicy: filterDataByHighestPercentage(cabinPricing.pricing.cancellationSchedule || [], isNonRefundable),
      /** @deprecated use paymentScheduleDetails instead */
      paymentSchedule: cabinPricing.pricing.paymentSchedule,
      ...(!!onboardCredit && {
        onboardCredits: {
          amount: onboardCredit.amount,
          currencyCode: onboardCredit.currency,
        },
      }),
    },
    /** @deprecated use paymentScheduleDetails instead */
    depositDetails: cabinPricing.depositDetails,
    paymentConfiguration: cabinPricing.paymentConfiguration,
    paymentScheduleDetails: cabinPricing.paymentScheduleDetails,
  }
}

const calcArrivalDate = (departureDate: string, duration: number): string => {
  const date = new Date(departureDate)
  const returnDate = addDays(date, duration)
  return isoDateFormat(returnDate)
}

export const cruisePromoBannersMap = (cruisePromoBanners: CruisesContract.PromoBanner[]): App.CruisePromoBanner[] => {
  return cruisePromoBanners.map(cruisePromoBanner => ({
    cruiseLineId: cruisePromoBanner.cruiseLineId,
    cruiseShipId: cruisePromoBanner.shipId,
    startDate: cruisePromoBanner.startDate,
    endDate: cruisePromoBanner.endDate,
    rateCode: cruisePromoBanner.rateCode,
    queryFilters: cruisePromoBanner.queryFilters,
    imageId: cruisePromoBanner.imageId,
    mobileImageId: cruisePromoBanner.mobileImageId,
  }))
}
